import { ref, computed, toRefs } from "vue";
import gql from "fraql";
import { getFederationConfiguration } from "@/composables/masterdata/config";
import allowedMutation from "@/utils/allowedMutation";
import { FipaEventStatus } from "@/types/event/event";
import { truncate } from "lodash-es";
export const CompetitionNameFragment = gql `
  fragment _ on Competition {
    name
    competitionLevelId
    competitionLevel @client {
      id
      name
    }
    difficultyLevelId
    difficultyLevel @client {
      id
      displayName
    }
    competitionRuleId
    competitionRule @client {
      id
      name
    }
  }
`;
export function competitionName(competition) {
    var _a, _b, _c;
    let name = [
        competition.name,
        (_a = competition.competitionLevel) === null || _a === void 0 ? void 0 : _a.name,
        (_b = competition.difficultyLevel) === null || _b === void 0 ? void 0 : _b.displayName,
        (_c = competition.competitionRule) === null || _c === void 0 ? void 0 : _c.name
    ]
        .filter(Boolean)
        .join(" | ");
    return truncate(name, { length: 45, separator: " ", omission: ".." });
}
export function useCompetitionTableHelper(props) {
    const { outsideOfCompetitionRuleAllowed } = getFederationConfiguration();
    const disabledCompetitionStorage = ref([]);
    const { event, competitions, day, index } = toRefs(props);
    const competitionTotalsCount = computed(() => {
        const totals = [
            {
                name: "totalStarted",
                value: totalStarted.value
            },
            {
                name: "totalConfirmed",
                value: totalConfirmed.value
            }
        ];
        if (outsideOfCompetitionRuleAllowed) {
            totals.push({
                name: "totalHCs",
                value: totalHcCount.value
            });
        }
        return totals;
    });
    const totalHcCount = computed(() => {
        return competitions.value.reduce((total, c) => {
            if (c.outsideOfCompetitionAllowed) {
                total += c.entriesRegistrationConfirmedOutsideOfCompetitionCount || 0;
            }
            return total;
        }, 0);
    });
    const canAdd = computed(() => {
        return allowedMutation(event.value.allowedMutations, "eventCompetitionCreateOrUpdate");
    });
    const dailyStat = computed(() => {
        var _a, _b;
        return ((_b = (_a = event.value.dailyStats) === null || _a === void 0 ? void 0 : _a.find(x => x.date == day.value)) !== null && _b !== void 0 ? _b : {
            athletesRegistrationConfirmedCount: 0,
            horsesRegistrationConfirmedCount: 0,
            equipagesRegistrationConfirmedCount: 0
        });
    });
    const resultsPublished = computed(() => {
        return event.value.status === FipaEventStatus.ResultsPublished;
    });
    const competitionDayNumber = computed(() => {
        return index.value + 1;
    });
    const eventLimit = computed(() => {
        return event.value.eventLimits.find(limit => limit.limitScope == "COMPETITION_DAY" &&
            limit.competitionDayNumber == competitionDayNumber.value);
    });
    const currentEntriesStat = computed(() => {
        if (eventLimit.value) {
            const arr = [
                {
                    name: "entriesTotal",
                    value: eventLimit.value.entriesRegistrationConfirmedCount
                }
            ];
            if (eventLimit.value.maxNumberOfEntries) {
                arr.push({
                    name: "entriesMax",
                    value: eventLimit.value.maxNumberOfEntries
                });
            }
            return arr;
        }
        else {
            return [
                {
                    name: "registrationsCount",
                    value: registrationsTotal.value
                }
            ];
        }
    });
    const currentAthletesStat = computed(() => {
        if (eventLimit.value) {
            const arr = [
                {
                    name: "athletesTotal",
                    value: eventLimit.value.athletesRegistrationConfirmedCount
                }
            ];
            if (eventLimit.value.maxNumberOfAthletes) {
                arr.push({
                    name: "athletesMax",
                    value: eventLimit.value.maxNumberOfAthletes
                });
            }
            return arr;
        }
        else {
            return [
                {
                    name: "athlets",
                    value: dailyStat.value.athletesRegistrationConfirmedCount
                }
            ];
        }
    });
    const canLimitEntriesPerDay = computed(() => {
        return allowedMutation(event.value.allowedMutations, "eventLimitEntriesForDay");
    });
    const canSplit = computed(() => {
        return competitions.value.some(x => allowedMutation(x.allowedMutations, "eventCompetitionSplit"));
    });
    const registrationsTotal = computed(() => {
        let total = 0;
        competitions.value.forEach(x => (total += x.registrationsCount));
        return total;
    });
    const totalStarted = computed(() => {
        let total = 0;
        competitions.value.forEach(x => (total += x.entriesRegisteredCount));
        return total;
    });
    const totalConfirmed = computed(() => {
        let total = 0;
        competitions.value.forEach(x => (total += x.entriesRegistrationConfirmedCount));
        return total;
    });
    const totalWithdrawn = computed(() => {
        let total = 0;
        competitions.value.forEach(x => (total += x.entriesUnregisteredWithdrawnCount));
        return total;
    });
    const disabledCompetitionIds = computed({
        get() {
            return disabledCompetitionStorage.value;
        },
        set(v) {
            if (disabledCompetitionStorage.value.includes(v)) {
                disabledCompetitionStorage.value =
                    disabledCompetitionStorage.value.filter(x => x != v);
            }
            else {
                disabledCompetitionStorage.value = [
                    ...disabledCompetitionStorage.value,
                    v
                ];
            }
        }
    });
    function competitionEntriesCount(competition) {
        const counts = [
            {
                name: "entriesRegisteredCount",
                value: competition.entriesRegisteredCount
            },
            {
                name: "entriesRegistrationConfirmedCount",
                value: competition.entriesRegistrationConfirmedCount
            }
        ];
        if (outsideOfCompetitionRuleAllowed) {
            counts.push({
                name: "entriesRegistrationConfirmedOutsideOfCompetitionCount",
                value: competition.entriesRegistrationConfirmedOutsideOfCompetitionCount
            });
        }
        return counts;
    }
    function competitionDisabled(competition) {
        if (event.value.publicRegistrationsDisabled ||
            ["cancelled", "locked", "registration_limit_reached"].includes(competition.status))
            return "alert--background-color";
    }
    function competitionMobileDetails(competition) {
        var _a, _b, _c;
        let name = [
            (_a = competition.competitionLevel) === null || _a === void 0 ? void 0 : _a.name,
            (_b = competition.difficultyLevel) === null || _b === void 0 ? void 0 : _b.displayName,
            (_c = competition.competitionRule) === null || _c === void 0 ? void 0 : _c.name
        ]
            .filter(Boolean)
            .join(" | ");
        return truncate(name, { length: 40, separator: " ", omission: "..." });
    }
    return {
        competitionTotalsCount,
        canAdd,
        dailyStat,
        resultsPublished,
        competitionDayNumber,
        eventLimit,
        currentEntriesStat,
        currentAthletesStat,
        canLimitEntriesPerDay,
        canSplit,
        registrationsTotal,
        totalStarted,
        totalConfirmed,
        totalWithdrawn,
        totalHcCount,
        disabledCompetitionIds,
        outsideOfCompetitionRuleAllowed,
        competitionEntriesCount,
        competitionDisabled,
        competitionName,
        competitionMobileDetails
    };
}
